import { z } from 'zod';
import * as dt from './data.types.js';
import { OCR_RESOLUTION_METHOD } from './data.types.js';
import { resolvedFingerprintSchema, ocrPhaseSchema } from './container-slot-scan.schemas.js';

const integerIdSchema = z.coerce.number().int().positive();
const uuidSchema = z.string().uuid();

export const fingerprintInferenceBatchContextSchema = z.nativeEnum(
  dt.FINGERPRINT_INFERENCE_BATCH_CONTEXT,
);

export const fingerprintInferenceBatchPhaseSchema = z.nativeEnum(
  dt.FINGERPRINT_INFERENCE_BATCH_PHASE,
);

export const createFingerprintInferenceBatchRequestSchema = z.object({
  objectKeys: z.array(z.string().min(1)).min(1).max(10),
  context: fingerprintInferenceBatchContextSchema,
  waferId: integerIdSchema.optional(),
  containerSlotScanJobId: uuidSchema.optional(),
  clientRequestId: z.string().min(1).max(255).optional(),
  inferenceProvider: z.string().optional(),
  /** Block POST until batch completes (single HTTP round-trip for Analyze UI). */
  waitTimeoutMs: z.number().int().min(0).max(120_000).optional(),
});


export const fingerprintInferenceBatchImageProgressSchema = z.object({
  id: uuidSchema,
  fileId: integerIdSchema,
  sortOrder: z.number().int(),
  objectKey: z.string(),
  ocrPhase: ocrPhaseSchema,
  fingerprint: z.string().optional(),
  confidence: z.number().min(0).max(1).optional(),
  blurScore: z.number().optional(),
  ocrErrorMessage: z.string().optional(),
});

export const fingerprintInferenceBatchProgressSchema = z.object({
  batchId: uuidSchema,
  phase: fingerprintInferenceBatchPhaseSchema,
  context: z.string(),
  waferId: integerIdSchema.optional(),
  phaseError: z.string().optional(),
  images: z.array(fingerprintInferenceBatchImageProgressSchema),
  resolvedFingerprint: resolvedFingerprintSchema.optional(),
});


export const createFingerprintInferenceBatchResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
  data: z.object({
    batchId: uuidSchema,
    /** True when SQS messages were sent before the HTTP response returned. */
    enqueued: z.boolean().optional(),
    done: z.boolean().optional(),
    progress: fingerprintInferenceBatchProgressSchema.optional(),
  }),
});

export const fingerprintInferenceBatchProgressResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
  data: fingerprintInferenceBatchProgressSchema,
});

/** Lightweight poll payload — no per-image rows. */
export const fingerprintInferenceBatchStatusSchema = z.object({
  batchId: uuidSchema,
  phase: fingerprintInferenceBatchPhaseSchema,
  totalImages: z.number().int().nonnegative(),
  terminalImages: z.number().int().nonnegative(),
  done: z.boolean(),
  /** Present when done=true so clients can skip a second GET /batches/:id. */
  progress: fingerprintInferenceBatchProgressSchema.optional(),
});


export const fingerprintInferenceBatchStatusResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
  data: fingerprintInferenceBatchStatusSchema,
});

export const retryFingerprintInferenceBatchRequestSchema = z.object({
  batchImageIds: z.array(uuidSchema).min(1).optional(),
});


export const applyFingerprintInferenceBatchRequestSchema = z.object({
  usePendingFingerprint: z.boolean().optional(),
});

export const fingerprintInferenceAnalysisResultSchema = z.object({
  ocrResults: z.array(
    z.object({
      text: z.string(),
      confidence: z.number(),
      imageId: z.string(),
      blurScore: z.number(),
    }),
  ),
  resolution: z
    .object({
      finalValue: z.string(),
      finalConfidence: z.number(),
      resolutionMethod: z.nativeEnum(OCR_RESOLUTION_METHOD),
      requiredUserReview: z.boolean(),
      explanation: z.string(),
    })
    .optional(),
  allImagesLowQuality: z.boolean(),
  warnings: z.array(z.string()).optional(),
});


export const runFingerprintInferenceAnalysisResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
  data: fingerprintInferenceAnalysisResultSchema,
});

export const fingerprintInferenceBatchIdParamSchema = z.object({
  batchId: uuidSchema,
});
// Inferred types
export type CreateFingerprintInferenceBatchRequest = z.infer<
  typeof createFingerprintInferenceBatchRequestSchema
>;
export type FingerprintInferenceAnalysisResult = z.infer<
  typeof fingerprintInferenceAnalysisResultSchema
>;
export type FingerprintInferenceBatchProgress = z.infer<
  typeof fingerprintInferenceBatchProgressSchema
>;
export type FingerprintInferenceBatchStatus = z.infer<
  typeof fingerprintInferenceBatchStatusSchema
>;
export type RetryFingerprintInferenceBatchRequest = z.infer<
  typeof retryFingerprintInferenceBatchRequestSchema
>;
